// The live numbers on a venue card (spec/13 §2.4): PCs in use of those offered, from the floor's own live read
// (`GET …/venues/{v}/floor/live`, F-ANL-02). It needs `shifts:day_close`, so a member without it gets a card with no
// numbers — never a zero, never an error. Read only for a handful of venues: a member with dozens sees their cards
// without numbers rather than a burst of requests.

import { useQueries } from '@tanstack/react-query';

import { useApi } from '../api/client';
import type { MyVenue } from '../api/me';

/** More venues than this and the cards carry no numbers. */
export const LIVE_NUMBERS_UP_TO = 12;

export interface VenueLive {
  readonly inUse: number;
  readonly total: number;
}

export function useVenueLive(venues: readonly MyVenue[]): ReadonlyMap<string, VenueLive> {
  const api = useApi();
  const wanted = venues.length <= LIVE_NUMBERS_UP_TO ? venues : [];
  const results = useQueries({
    queries: wanted.map((venue) => ({
      queryKey: ['venue-card-live', venue.id] as const,
      queryFn: () => api.getFloorLive({ path: { organizationId: venue.organizationId, venueId: venue.id } }),
      staleTime: 30_000,
      retry: false,
      refetchOnWindowFocus: false,
    })),
  });
  const live = new Map<string, VenueLive>();
  wanted.forEach((venue, index) => {
    const data = results[index]?.data;
    if (data !== undefined) live.set(venue.id, { inUse: data.stationsInSession, total: data.stationsOffered });
  });
  return live;
}
